import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export const useGoogleDocs = () => {
  const { api } = useAuth();
  const [gdocsExporting, setGdocsExporting] = useState(false);
  const resolveRef = useRef(null);

  // Listen for postMessage from the OAuth popup
  useEffect(() => {
    const handler = (event) => {
      if (event.data?.type === "google-auth-success") {
        resolveRef.current?.({ success: true });
        resolveRef.current = null;
      } else if (event.data?.type === "google-auth-error") {
        resolveRef.current?.({ success: false, error: event.data.error });
        resolveRef.current = null;
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  const connectGoogle = useCallback(async () => {
    try {
      const resp = await api.post("/google/init-auth");
      const url = resp.data.authorization_url;

      const popup = window.open(url, "google-auth", "width=520,height=640,left=200,top=100");

      return new Promise((resolve) => {
        resolveRef.current = resolve;

        const timeout = setTimeout(() => {
          resolve({ success: false, error: "timeout" });
          resolveRef.current = null;
        }, 120_000);

        const check = setInterval(() => {
          if (popup?.closed) {
            clearInterval(check);
            clearTimeout(timeout);
            if (resolveRef.current) {
              resolve({ success: false, error: "popup_closed" });
              resolveRef.current = null;
            }
          }
        }, 500);
      });
    } catch {
      return { success: false, error: "init_failed" };
    }
  }, [api]);

  /**
   * Full export flow: check connection ➜ connect if needed ➜ create doc.
   * @param {{ title: string, sections: Record<string,string>, section_names?: string[] }} opts
   */
  const exportToGoogleDocs = useCallback(
    async ({ title, sections, section_names }) => {
      setGdocsExporting(true);
      try {
        // 1. Check if already connected
        const statusResp = await api.get("/google/status");

        if (!statusResp.data.connected) {
          toast.info("Connecting to Google Docs...");
          const result = await connectGoogle();
          if (!result.success) {
            if (result.error !== "popup_closed") {
              toast.error("Could not connect Google account. Please try again.");
            }
            return null;
          }
          toast.success("Google account connected!");
        }

        // 2. Create the doc
        toast.info("Creating your Google Doc...");
        const resp = await api.post("/google/export-doc", {
          title,
          sections,
          section_names: section_names || Object.keys(sections),
        });

        if (resp.data.success) {
          toast.success("Google Doc created! Opening now...", { duration: 4000 });
          window.open(resp.data.document_url, "_blank");
          return resp.data;
        }
        return null;
      } catch (err) {
        const detail = err.response?.data?.detail || "";
        if (err.response?.status === 401 && typeof detail === "string" && detail.includes("reconnect")) {
          toast.error("Google authorization expired. Please try again to reconnect.");
        } else {
          toast.error("Failed to export to Google Docs.");
        }
        return null;
      } finally {
        setGdocsExporting(false);
      }
    },
    [api, connectGoogle],
  );

  return { exportToGoogleDocs, gdocsExporting };
};
